import React from "react";
import {
  FaAt,
  FaEdit,
  FaEnvelope,
  FaPaperPlane,
  FaPhoneAlt,
  FaPlus,
  FaQrcode,
  FaTrash,
  FaUserClock,
  FaUsers,
  FaUserEdit,
} from "react-icons/fa";

import {
  DEFAULT_USER_AVATAR,
  formatBirthDate,
  getAdherentFullName,
  getGroupColorStyle,
  getStatusClass,
  getStatusLabel,
  normalizePhoneForHref,
} from "../utils/groupsUtils";

function GroupActionButton({
  icon: Icon,
  children,
  onClick,
  disabled,
  className = "",
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 ${className}`}
    >
      <Icon />
      {children}
    </button>
  );
}

function ContactAction({
  href,
  icon: Icon,
  children,
  title,
  enabledClassName,
}) {
  if (!href) {
    return (
      <button
        type="button"
        disabled
        title={title}
        className="inline-flex min-h-9 cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-slate-200 px-3 py-1 text-xs font-bold text-slate-400 opacity-75 dark:bg-white/10 dark:text-white/30"
      >
        <Icon />
        {children}
      </button>
    );
  }

  return (
    <a
      href={href}
      title={title}
      className={`inline-flex min-h-9 items-center justify-center gap-2 rounded-2xl px-3 py-1 text-xs font-bold text-white transition hover:-translate-y-0.5 ${enabledClassName}`}
    >
      <Icon />
      {children}
    </a>
  );
}

function AdherentRow({ adherent, onOpenStatus, onMessage, onEdit }) {
  const fullName = getAdherentFullName(adherent);

  const phone = String(adherent?.tel_adherent || "").trim();

  const normalizedPhone = normalizePhoneForHref(phone);

  const email = String(adherent?.mail_adherent || "").trim();

  const emailSubject = encodeURIComponent(
    `Judo Coach Pro - Contact concernant ${fullName}`,
  );

  const phoneHref = normalizedPhone ? `tel:${normalizedPhone}` : "";

  const emailHref = email
    ? `mailto:${email}?subject=${emailSubject}`
    : "";

  return (
    <article className="rounded-[1.25rem] border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-950/60">
      <div className="flex items-start gap-3">
        <img
          src={adherent?.photo_url || DEFAULT_USER_AVATAR}
          alt={fullName}
          className="h-12 w-12 rounded-2xl object-cover"
          onError={(event) => {
            event.currentTarget.src = DEFAULT_USER_AVATAR;
          }}
        />

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
            {fullName}
          </p>

          <p className="mt-1 text-xs font-medium text-slate-500 dark:text-white/45">
            Né(e) le {formatBirthDate(adherent?.date_naissance)}
          </p>

          {adherent?.club && (
            <p className="mt-1 truncate text-xs font-medium text-slate-500 dark:text-white/45">
              Club : {adherent.club}
            </p>
          )}

          {(phone || email) && (
            <div className="mt-2 flex flex-col gap-1 text-xs font-medium text-slate-500 dark:text-white/45">
              {phone && (
                <span className="inline-flex min-w-0 items-center gap-2">
                  <FaPhoneAlt className="shrink-0 text-[10px]" />

                  <span className="truncate">{phone}</span>
                </span>
              )}

              {email && (
                <span className="inline-flex min-w-0 items-center gap-2">
                  <FaAt className="shrink-0 text-[10px]" />

                  <span className="truncate">{email}</span>
                </span>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onOpenStatus}
              className={`inline-flex min-h-9 items-center justify-center gap-2 rounded-2xl px-3 py-1 text-xs font-bold transition hover:-translate-y-0.5 ${getStatusClass(
                adherent?.status,
              )}`}
            >
              <FaUserClock />
              {getStatusLabel(adherent?.status)}
            </button>

            <button
              type="button"
              onClick={onEdit}
              className="inline-flex min-h-9 items-center justify-center gap-2 rounded-2xl bg-slate-800 px-3 py-1 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-950 dark:bg-white/10 dark:hover:bg-white/15"
            >
              <FaUserEdit />
              Modifier
            </button>

            <button
              type="button"
              onClick={onMessage}
              className="inline-flex min-h-9 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-3 py-1 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-sky-700"
            >
              <FaEnvelope />
              Message
            </button>

            <ContactAction
              href={phoneHref}
              icon={FaPhoneAlt}
              title={
                phone
                  ? `Appeler ${fullName} au ${phone}`
                  : "Aucun numéro de téléphone renseigné"
              }
              enabledClassName="bg-emerald-600 hover:bg-emerald-700"
            >
              Appeler
            </ContactAction>

            <ContactAction
              href={emailHref}
              icon={FaAt}
              title={
                email
                  ? `Envoyer un e-mail à ${email}`
                  : "Aucune adresse e-mail renseignée"
              }
              enabledClassName="bg-indigo-600 hover:bg-indigo-700"
            >
              E-mail
            </ContactAction>
          </div>
        </div>
      </div>
    </article>
  );
}

function GroupCard({
  group,
  adherents = [],
  savingAction,
  onInvite,
  onEdit,
  onDelete,
  onCreateAdherent,
  onOpenStatus,
  onMessageAdherent,
  onMessageGroup,
  onEditAdherent,
}) {
  const colorStyle = getGroupColorStyle(group?.color_groupe);

  const isDeleting = savingAction === `delete-group-${group.id}`;
  const isInviting = savingAction === `invite-group-${group.id}`;

  return (
    <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className={`bg-gradient-to-br ${colorStyle.header} p-5 text-white`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/65">
              Groupe coach
            </p>

            <h2 className="mt-2 truncate font-titre text-3xl font-bold">
              {group?.nom || "Groupe"}
            </h2>

            <p className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-white/75">
              <FaUsers />
              {adherents.length} judoka{adherents.length > 1 ? "s" : ""}
            </p>
          </div>

          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.25rem] bg-white/15 text-2xl backdrop-blur-xl">
            <FaUsers />
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
          <GroupActionButton
            icon={FaPlus}
            onClick={onCreateAdherent}
            className="bg-orange-600 hover:bg-orange-700"
          >
            Ajouter
          </GroupActionButton>

          <GroupActionButton
            icon={FaQrcode}
            onClick={onInvite}
            disabled={isInviting}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isInviting ? "Lien..." : "Inviter"}
          </GroupActionButton>

          <GroupActionButton
            icon={FaEdit}
            onClick={onEdit}
            className="bg-sky-600 hover:bg-sky-700"
          >
            Éditer
          </GroupActionButton>

          <GroupActionButton
            icon={FaPaperPlane}
            onClick={onMessageGroup}
            disabled={!adherents.length}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Groupe
          </GroupActionButton>

          <GroupActionButton
            icon={FaTrash}
            onClick={onDelete}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? "Suppression..." : "Supprimer"}
          </GroupActionButton>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between gap-4">
            <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
              Judokas
            </h3>

            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${colorStyle.badge}`}
            >
              {adherents.length}
            </span>
          </div>

          {adherents.length > 0 ? (
            <div className="mt-4 space-y-3">
              {adherents.map((adherent) => (
                <AdherentRow
                  key={adherent.id}
                  adherent={adherent}
                  onOpenStatus={() => onOpenStatus(adherent)}
                  onEdit={() => onEditAdherent(adherent)}
                  onMessage={() => onMessageAdherent(adherent)}
                />
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-white/10 dark:bg-white/[0.03]">
              <p className="text-sm font-bold text-slate-700 dark:text-white/70">
                Aucun judoka dans ce groupe.
              </p>

              <p className="mt-1 text-xs font-medium text-slate-500 dark:text-white/45">
                Ajoute un judoka manuellement ou utilise le bouton Inviter pour
                partager un lien ou un QR code.
              </p>

              <button
                type="button"
                onClick={onCreateAdherent}
                className="mt-4 inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-orange-700"
              >
                <FaPlus />
                Ajouter un judoka
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export default GroupCard;